import { useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';
import { OfferConfirmAction } from '../../constants/offer-confirm-action.enum';

export interface OfferConfirmCandidate {
  mail: string;
  action: OfferConfirmAction;
}

export interface OfferConfirmDialogProps {
  /** `null` closes the dialog. */
  candidate: OfferConfirmCandidate | null;
  onClose: () => void;
  /** Resolves `true` when the dialog may close. */
  onConfirm: () => Promise<boolean>;
}

const CONTENT: Record<OfferConfirmAction, { title: string; description: (mail: string) => string; confirmLabel: string }> = {
  [OfferConfirmAction.DELETE]: {
    title: 'Entwurf löschen?',
    description: (mail) => `Der Entwurf für ${mail} wird endgültig gelöscht. Er wurde nie versendet.`,
    confirmLabel: 'Löschen'
  },
  [OfferConfirmAction.REVOKE]: {
    title: 'Angebot zurückziehen?',
    description: (mail) =>
      `Der Link an ${mail} funktioniert danach nicht mehr – der Kunde bekommt keine Mail. Das Angebot bleibt als „Zurückgezogen“ unter „Abgeschlossen“.`,
    confirmLabel: 'Zurückziehen'
  },
  [OfferConfirmAction.ARCHIVE]: {
    title: 'Angebot archivieren?',
    description: (mail) =>
      `Das Angebot für ${mail} wandert ins Archiv und kann dort nur noch angesehen werden – nicht mehr versenden, zurückziehen oder korrigieren. Bei einem angenommenen Angebot pflegst du die Kundendaten danach wieder auf der Kundenseite.`,
    confirmLabel: 'Archivieren'
  }
};

/** Last check before a draft is deleted, a sent offer revoked or a closed one archived – none of it can be undone. */
const OfferConfirmDialog = ({ candidate, onClose, onConfirm }: OfferConfirmDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const content = candidate ? CONTENT[candidate.action] : null;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      if (await onConfirm()) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-md min-w-[440px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-[22px] font-bold text-[#12282A]">{content?.title}</DialogTitle>
          <DialogDescription className="text-[14px] text-[#888]">{candidate && content?.description(candidate.mail)}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button variant="destructive" className="text-white" onClick={handleConfirm} disabled={isSubmitting}>
            {content?.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OfferConfirmDialog;
